import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  Table as ChakraTable,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Icon,
  InputGroup,
  InputLeftElement,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from "@chakra-ui/react";
import { FiSearch, FiChevronUp, FiChevronDown, FiChevronLeft, FiChevronRight, FiX } from "react-icons/fi";

export interface TableColumn<T = any> {
  key: string;
  label: string;
  sortable?: boolean;
  width?: string;
  className?: string;
  render?: (item: T, index: number) => React.ReactNode;
  headerRender?: () => React.ReactNode;
}

export interface TableFilter {
  key: string;
  type: "text" | "select";
  label: string;
  placeholder?: string;
  options?: Array<{ value: string; label: string }>;
  width?: string;
}

export interface TablePagination {
  total: number;
  offset: number;
  has_more: boolean;
}

export interface TableProps<T = any> {
  // Data
  data: T[];
  columns: TableColumn<T>[];
  loading?: boolean;
  error?: string | null;

  // Pagination
  pagination: TablePagination;
  limit?: number;
  onPageChange?: (offset: number) => void;

  // Sorting
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSort?: (sortBy: string, sortOrder: "asc" | "desc") => void;

  // Filtering
  filters?: TableFilter[];
  filterValues?: Record<string, string>;
  onFilterChange?: (filters: Record<string, string>) => void;
  onApplyFilters?: () => void;
  onClearFilters?: () => void;

  // Selection
  selectable?: boolean;
  selectedItems?: Set<string>;
  onSelectionChange?: (selectedItems: Set<string>) => void;
  getItemId?: (item: T) => string;
  singleSelection?: boolean;
  hideSelectionCount?: boolean;

  // Row actions
  onRowClick?: (item: T) => void;
  rowActions?: (item: T) => React.ReactNode;

  // Custom header actions
  headerActions?: React.ReactNode;

  // Styling
  className?: string;
  emptyMessage?: string;
  emptySubMessage?: string;
}

const Table = <T extends Record<string, any>>({
  data,
  columns,
  loading = false,
  error = null,
  pagination,
  limit = 50,
  onPageChange,
  sortBy,
  sortOrder = "asc",
  onSort,
  filters = [],
  filterValues: externalFilterValues,
  onFilterChange,
  onApplyFilters,
  onClearFilters,
  selectable = false,
  selectedItems = new Set(),
  onSelectionChange,
  getItemId = (item) => item.id,
  singleSelection = false,
  hideSelectionCount = false,
  onRowClick,
  rowActions,
  headerActions,
  className = "eva-table-container",
  emptyMessage = "No data found",
  emptySubMessage = "Try adjusting your search criteria",
}: TableProps<T>) => {
  const [internalFilterValues, setInternalFilterValues] = useState<Record<string, string>>({});
  const shouldAutoApplyRef = useRef(false);
  const debounceTimerRef = useRef<Record<string, NodeJS.Timeout>>({});
  const latestFilterValuesRef = useRef<Record<string, string>>({}); // Add this ref

  // Use external filter values if provided, otherwise use internal state
  const filterValues = externalFilterValues !== undefined ? externalFilterValues : internalFilterValues;

  // Keep the ref in sync with current filter values
  useEffect(() => {
    latestFilterValuesRef.current = filterValues;
  }, [filterValues]);

  // Cleanup debounce timers on unmount
  useEffect(() => {
    return () => {
      const timers = debounceTimerRef.current;
      Object.values(timers).forEach(timer => {
        if (timer) clearTimeout(timer);
      });
    };
  }, []);

  // Auto-apply filters when filter values change and shouldAutoApply is true
  useEffect(() => {
    if (shouldAutoApplyRef.current && onApplyFilters) {
      onApplyFilters();
      shouldAutoApplyRef.current = false;
    }
  }, [externalFilterValues, internalFilterValues, onApplyFilters]);

  // Debounced handler for text filter changes
  const handleTextFilterChange = (key: string, value: string) => {
    // Clear existing timer for this filter
    if (debounceTimerRef.current[key]) {
      clearTimeout(debounceTimerRef.current[key]);
      delete debounceTimerRef.current[key];
    }

    // Calculate new filter values
    const newFilterValues = { ...filterValues, [key]: value };

    // Update internal state only if external filter values are not provided
    if (externalFilterValues === undefined) {
      setInternalFilterValues(newFilterValues);
    }

    // Immediately notify parent of filter change
    onFilterChange?.(newFilterValues);

    // Set new timer to apply filters after debounce delay
    debounceTimerRef.current[key] = setTimeout(() => {
      // Apply filters only if onApplyFilters is provided
      if (onApplyFilters) {
        onApplyFilters();
      }
      delete debounceTimerRef.current[key];
    }, 500);
  };

  // Handle filter value changes (for non-debounced filters like select)
  const handleFilterChange = (key: string, value: string, autoApply: boolean = false) => {
    const newFilterValues = { ...filterValues, [key]: value };

    // Update internal state only if external filter values are not provided
    if (externalFilterValues === undefined) {
      setInternalFilterValues(newFilterValues);
    }

    onFilterChange?.(newFilterValues);

    // Set flag to auto-apply on next render
    if (autoApply && onApplyFilters) {
      shouldAutoApplyRef.current = true;
    }
  };

  // Handle selection
  const handleSelectItem = (itemId: string, isSelected: boolean) => {
    if (!onSelectionChange) return;

    if (singleSelection) {
      // For single selection, replace the entire selection with the new item
      if (isSelected) {
        onSelectionChange(new Set([itemId]));
      } else {
        onSelectionChange(new Set());
      }
    } else {
      // For multiple selection, add/remove items as before
      const newSelectedItems = new Set(selectedItems);
      if (isSelected) {
        newSelectedItems.add(itemId);
      } else {
        newSelectedItems.delete(itemId);
      }
      onSelectionChange(newSelectedItems);
    }
  };

  const handleSelectAll = (isSelected: boolean) => {
    if (!onSelectionChange || singleSelection) return;

    if (isSelected) {
      const allItemIds = new Set(data.map(item => getItemId(item)).filter(Boolean));
      onSelectionChange(allItemIds);
    } else {
      onSelectionChange(new Set());
    }
  };

  // Handle sorting
  const handleSort = (columnKey: string) => {
    if (!onSort) return;

    const newSortOrder = sortBy === columnKey && sortOrder === "asc" ? "desc" : "asc";
    onSort(columnKey, newSortOrder);
  };

  // Pagination calculations
  const currentPage = Math.floor((pagination.offset || 0) / limit) + 1;
  const totalPages = Math.ceil((pagination.total || 0) / limit);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      const newOffset = (currentPage - 2) * limit;
      onPageChange?.(newOffset);
    }
  };

  const handleNextPage = () => {
    if (pagination.has_more) {
      const newOffset = (pagination.offset || 0) + limit;
      onPageChange?.(newOffset);
    }
  };

  // Render filter header
  const renderFilterHeader = (totalCount: number) => (
    <HStack spacing={3} className="eva-filters-card" w="100%" flexWrap="wrap" align="stretch" justify="space-between">
      {selectable && selectedItems.size > 0 && headerActions && !hideSelectionCount ? (
        // Selection mode header - only show when there are header actions
        <HStack spacing={3} flexWrap="wrap" align="center">
          <Text color="fg.default" fontSize="sm" fontWeight="bold" mb={0}>
            {selectedItems.size} item{selectedItems.size !== 1 ? "s" : ""} selected
          </Text>
          {headerActions}
        </HStack>
      ) : (
        // Normal filter header
        <>
          {selectable && selectedItems.size > 0 && !singleSelection && !hideSelectionCount && (
            <Text color="purple.700" fontSize="sm" fontWeight="medium" mr={4} mb={0}>
              {selectedItems.size} item{selectedItems.size !== 1 ? "s" : ""} selected
            </Text>
          )}
          <HStack spacing={3} w="100%" flexWrap="wrap" align="stretch">
            {filters.map((filter) => (
              <Box key={filter.key} flex="1 1 220px" minW="160px" maxW={filter.width || "360px"}>
                {filter.type === "text" ? (
                  <InputGroup w="100%" borderColor="gray.600" borderRadius="md">
                    <InputLeftElement pointerEvents="none">
                      <Icon as={FiSearch} color="gray.600" />
                    </InputLeftElement>
                    <Input
                      size="sm"
                      placeholder={filter.placeholder || `Search ${filter.label.toLowerCase()}...`}
                      value={filterValues[filter.key] || ""}
                      onChange={(e) => handleTextFilterChange(filter.key, e.target.value)}
                    />
                  </InputGroup>
                ) : (
                  <Menu>
                    <MenuButton
                      as={Button}
                      size="sm"
                      variant="outline"
                      w="100%"
                      textAlign="left"
                      rightIcon={<Icon as={FiChevronDown} boxSize={4} />}
                      fontWeight="normal"
                      fontSize="sm"
                      bg="white"
                      _hover={{ bg: "gray.50" }}
                      _active={{ bg: "gray.100" }}
                      overflow="hidden"
                    >
                      <Text
                        as="span"
                        noOfLines={1}
                        display="block"
                        textAlign="left"
                        fontWeight="600"
                      >
                        {filter.options?.find(opt => opt.value === filterValues[filter.key])?.label ||
                          filter.placeholder ||
                          filter.label}
                      </Text>
                    </MenuButton>
                    <MenuList maxH="300px" overflowY="auto" zIndex={1500}>
                      {filter.options?.map(option => (
                        <MenuItem
                          key={option.value}
                          onClick={() => handleFilterChange(filter.key, option.value, true)}
                          fontSize="xs"
                          fontWeight="600"
                          color="gray.600"
                          bg={filterValues[filter.key] === option.value ? "purple.50" : "transparent"}
                          _hover={{ bg: "purple.50" }}
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                )}
              </Box>
            ))}

              {onClearFilters && (
                <Button
                  onClick={() => {
                    // Reset internal filter values if external filter values are not provided
                    if (externalFilterValues === undefined) {
                      setInternalFilterValues({});
                    }
                    onClearFilters();
                  }}
                  variant="ghost"
                  size="sm"
                  leftIcon={<Icon as={FiX} boxSize={4} />}
                  colorScheme="gray"
                  color="gray.600"
                  _hover={{
                    bg: "gray.100",
                    color: "gray.800",
                  }}
                  fontWeight="500"
                  flexShrink={0}
                >
                  Clear Filters
                </Button>
              )}
            {/* Total count aligned to the end, stays on same row when space allows */}
            <Box ml="auto" flexShrink={0} alignContent="center">
              <Text color="gray.600" fontSize="sm" mb={0}>
                Showing {totalCount} items
              </Text>
            </Box>
          </HStack>
        </>
      )}
    </HStack>
  );

  // Loading state
  if (loading && data.length === 0) {
    return (
      <Box className={className}>
        <VStack align="stretch">
          {renderFilterHeader(0)}
          <Box display="flex" justifyContent="center" alignItems="center" minH="400px">
            <VStack spacing={4}>
              <Spinner size="xl" color="purple.500" />
              <Text>Loading...</Text>
            </VStack>
          </Box>
        </VStack>
      </Box>
    );
  }

  // Empty state
  if (!loading && data.length === 0) {
    return (
      <Box className={className}>
        <VStack align="stretch">
          {renderFilterHeader(0)}
          <Box display="flex" justifyContent="center" alignItems="center" minH="300px" bg="gray.50" borderRadius="md">
            <VStack spacing={4}>
              <Text fontSize="lg" color="gray.500" fontWeight="medium">
                {emptyMessage}
              </Text>
              <Text fontSize="sm" color="gray.400" textAlign="center">
                {emptySubMessage}
              </Text>
            </VStack>
          </Box>
        </VStack>
      </Box>
    );
  }

  return (
    <Box className={className} height="100%" display="flex" flexDirection="column">
      <VStack align="stretch" spacing={4} flex="1" overflow="hidden">
        {/* Filters - Fixed */}
        <Box flexShrink={0}>
          {renderFilterHeader(data.length)}
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert status="error" flexShrink={0}>
            <AlertIcon />
            <Box>
              <AlertTitle>Error loading data!</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Box>
          </Alert>
        )}

        {/* Table - Scrollable Body */}
        <Box
          className="eva-table-wrapper eva-small-scrollbar"
          flex="1"
          overflowY="auto"
          overflowX="auto"
        >
          <ChakraTable variant="simple" size="md" maxWidth="100%">
            <Thead bg="gray.50" position="sticky" top={0} zIndex={1}>
              <Tr>
                {selectable && !singleSelection && (
                  <Th bg="purple.700" color="white" py={1} px={2} pb={1}
                  _hover={{ bg: "purple.600" }}
                  _active={{ bg: "purple.500" }}>
                    <input
                      type="checkbox"
                      checked={data.length > 0 && selectedItems.size === data.length}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      style={{ cursor: "pointer", margin: 0, marginBottom: 0, display: "block" }}
                    />
                  </Th>
                )}
                {selectable && singleSelection && (
                  <Th width="50px"
                  bg="purple.700" color="white" py={1}
                  _hover={{ bg: "purple.600" }}
                  _active={{ bg: "purple.500" }}>
                    {/* Empty header for single selection mode */}
                  </Th>
                )}
                {rowActions && <Th bg="purple.700" color="white" py={1} fontWeight="600" fontSize="sm">Actions</Th>}
                {columns.map((column) => (
                  <Th
                    key={column.key}
                    className={`${column.className || ""} ${column.sortable ? "eva-sortable-header" : ""}`}
                    width={column.width}
                    onClick={column.sortable ? () => handleSort(column.key) : undefined}
                    cursor={column.sortable ? "pointer" : "default"}
                    bg="purple.700"
                    color="white"
                    py={1}
                    _hover={{ bg: "purple.600" }}
                    _active={{ bg: "purple.500" }}
                  >
                    {column.headerRender ? (
                      column.headerRender()
                    ) : (
                      <HStack spacing={1}>
                        <Text mb="0" fontSize="sm" fontWeight="600">{column.label}</Text>
                        {column.sortable && sortBy === column.key && (
                          sortOrder === "asc" ? <FiChevronUp /> : <FiChevronDown />
                        )}
                      </HStack>
                    )}
                  </Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {data.map((item, index) => {
                const itemId = getItemId(item);
                return (
                  <Tr
                    key={itemId}
                    _hover={{ bg: "gray.50" }}
                    cursor={onRowClick ? "pointer" : "default"}
                    onClick={() => onRowClick?.(item)}
                  >
                    {rowActions && (
                      <Td className="eva-actions-column" onClick={(e) => e.stopPropagation()}>
                        {rowActions(item)}
                      </Td>
                    )}
                    {selectable && (
                      <Td className="eva-checkbox-column" onClick={(e) => e.stopPropagation()} px={2}>
                        <input
                          type={singleSelection ? "radio" : "checkbox"}
                          name={singleSelection ? "single-selection" : undefined}
                          checked={itemId ? selectedItems.has(itemId) : false}
                          onChange={(e) => itemId && handleSelectItem(itemId, e.target.checked)}
                          style={{ cursor: "pointer", margin: 0, marginBottom: 0, display: "block" }}
                        />
                      </Td>
                    )}
                    {columns.map((column) => (
                      <Td key={column.key} className={column.className}>
                        {column.render ? column.render(item, index) : item[column.key]}
                      </Td>
                    ))}
                  </Tr>
                );
              })}
            </Tbody>
          </ChakraTable>
        </Box>

        {/* Pagination Controls - Fixed */}
        {onPageChange && (
          <HStack
            justify="center"
            p={2}
            pb={0}
            borderTop="1px solid"
            borderColor="gray.200"
            flexShrink={0}
          >
            <Button
              size="xs"
              onClick={handlePreviousPage}
              isDisabled={currentPage <= 1}
              variant="outline"
            >
              <FiChevronLeft />
            </Button>
            <Text fontSize="sm" color="gray.600" mb={0} fontWeight="600">
              Page {currentPage} of {totalPages}
            </Text>
            <Button
              size="xs"
              onClick={handleNextPage}
              isDisabled={!pagination.has_more}
              variant="outline"
            >
              <FiChevronRight />
            </Button>
          </HStack>
        )}
      </VStack>
    </Box>
  );
};

export default Table;
